import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import AppShell from './components/layout/AppShell'
import { LegacyWorkspaceRedirect, TranslatedWorkspacePage } from './components/layout/WorkspacePage'
import RequireAuth from './components/auth/RequireAuth'
import { Toaster } from './components/ui/toaster'
import BootstrapRootToken from './pages/BootstrapRootToken'
import Login from './pages/Login'

// 每个子页都 lazy 拆 chunk,初屏只载 Login + 框架 + auth。
// Tremor + recharts 是主仓最重的 vendor(>800 KB),Vite 会自动 hoist 成共享
// 块给所有用 Tremor 的 page 复用(Overview / Finance / Reconciliation / Usage)。
const Overview = lazy(() => import('./pages/Overview'))
const Users = lazy(() => import('./pages/Users'))
const Admins = lazy(() => import('./pages/Admins'))
const Settings = lazy(() => import('./pages/Settings'))
const AiFunctions = lazy(() => import('./pages/AiFunctions'))
const AiModels = lazy(() => import('./pages/AiModels'))
const DispatchChains = lazy(() => import('./pages/DispatchChains'))
const Genres = lazy(() => import('./pages/Genres'))
const Locales = lazy(() => import('./pages/Locales'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Finance = lazy(() => import('./pages/Finance'))
const Reconciliation = lazy(() => import('./pages/Reconciliation'))
const ProviderFunding = lazy(() => import('./pages/ProviderFunding'))
const Anomalies = lazy(() => import('./pages/Anomalies'))
const Prompts = lazy(() => import('./pages/Prompts'))
const Usage = lazy(() => import('./pages/Usage'))
const AiUsageStats = lazy(() => import('./pages/AiUsageStats'))
const ReserveMetrics = lazy(() => import('./pages/ReserveMetrics'))

function ChunkFallback() {
  return (
    <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    </div>
  )
}

// 路由按任务域分组（总览 / 运营 / 账务 / AI 运行 / 系统）。
// 鉴权:除 /login 外全部包在 RequireAuth 下,未登录跳 /login 并记 from 路径。
// 全局 Toaster 挂在 Routes 兄弟节点，所有页面通过 Admin toast facade 调用。
export default function App() {
  return (
    <>
      <Toaster />
      <Routes>
        <Route path="/bootstrap/root-token" element={<BootstrapRootToken />} />
        <Route path="/login" element={<Login />} />

        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route index element={<Navigate to="/overview" replace />} />

            {/* 总览与用量共用一个观测工作区。 */}
            <Route
              path="/overview"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <TranslatedWorkspacePage
                    defaultView="summary"
                    navigationLabelKey="workspaceNavigation.overview"
                    views={[
                      {
                        value: 'summary',
                        labelKey: 'workspaceViews.overview.summary',
                        content: <Overview />
                      },
                      {
                        value: 'usage',
                        labelKey: 'nav.usage',
                        content: <Usage />
                      }
                    ]}
                  />
                </Suspense>
              }
            />

            {/* 运营：账号检索与异常处置共享用户工作区。 */}
            <Route
              path="/users"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <TranslatedWorkspacePage
                    defaultView="accounts"
                    navigationLabelKey="workspaceNavigation.users"
                    views={[
                      {
                        value: 'accounts',
                        labelKey: 'workspaceViews.users.accounts',
                        content: <Users />
                      },
                      {
                        value: 'anomalies',
                        labelKey: 'nav.anomalies',
                        content: <Anomalies />
                      }
                    ]}
                  />
                </Suspense>
              }
            />
            <Route
              path="/anomalies"
              element={<LegacyWorkspaceRedirect to="/users" view="anomalies" />}
            />
            <Route
              path="/ai-usage"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <AiUsageStats />
                </Suspense>
              }
            />

            {/* 账务：同一业务对象的报表与运行观测改为页内视图。 */}
            <Route
              path="/finance"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <TranslatedWorkspacePage
                    defaultView="profit"
                    navigationLabelKey="workspaceNavigation.finance"
                    views={[
                      {
                        value: 'profit',
                        labelKey: 'workspaceViews.finance.profit',
                        content: <Finance />
                      },
                      {
                        value: 'reserve',
                        labelKey: 'nav.reserveMetrics',
                        content: <ReserveMetrics />
                      }
                    ]}
                  />
                </Suspense>
              }
            />
            <Route
              path="/usage"
              element={<LegacyWorkspaceRedirect to="/overview" view="usage" />}
            />
            <Route
              path="/reconciliation"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <TranslatedWorkspacePage
                    defaultView="reconciliation"
                    navigationLabelKey="workspaceNavigation.providerOperations"
                    views={[
                      {
                        value: 'reconciliation',
                        labelKey: 'workspaceViews.providerOperations.reconciliation',
                        content: <Reconciliation />
                      },
                      {
                        value: 'funding',
                        labelKey: 'workspaceViews.providerOperations.funding',
                        content: <ProviderFunding />
                      }
                    ]}
                  />
                </Suspense>
              }
            />
            <Route
              path="/provider-funding"
              element={<LegacyWorkspaceRedirect to="/reconciliation" view="funding" />}
            />
            <Route
              path="/reserve-metrics"
              element={<LegacyWorkspaceRedirect to="/finance" view="reserve" />}
            />
            <Route
              path="/models"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <Pricing />
                </Suspense>
              }
            />

            {/* AI 运行 */}
            <Route
              path="/ai-functions"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <AiFunctions />
                </Suspense>
              }
            />
            <Route
              path="/ai-models"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <AiModels />
                </Suspense>
              }
            />
            <Route
              path="/dispatch-chains"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <DispatchChains />
                </Suspense>
              }
            />
            <Route
              path="/prompts"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <Prompts />
                </Suspense>
              }
            />

            {/* 系统 */}
            <Route
              path="/settings"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <Settings />
                </Suspense>
              }
            />
            <Route
              path="/locales"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <TranslatedWorkspacePage
                    defaultView="languages"
                    navigationLabelKey="workspaceNavigation.contentDictionaries"
                    views={[
                      {
                        value: 'languages',
                        labelKey: 'workspaceViews.contentDictionaries.languages',
                        content: <Locales />
                      },
                      {
                        value: 'genres',
                        labelKey: 'workspaceViews.contentDictionaries.genres',
                        content: <Genres />
                      }
                    ]}
                  />
                </Suspense>
              }
            />
            <Route
              path="/genres"
              element={<LegacyWorkspaceRedirect to="/locales" view="genres" />}
            />
            <Route
              path="/admins"
              element={
                <Suspense fallback={<ChunkFallback />}>
                  <Admins />
                </Suspense>
              }
            />

            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Route>
        </Route>
      </Routes>
    </>
  )
}
